import { useQuery } from '@tanstack/react-query';
import {
  OrganizationApiService,
  type OrganizationMemberRole,
} from '@/lib/services/organization-api';
import { useUIStore } from '@/store/ui-store';

/**
 * The signed-in user's role in the selected organization workspace, from the
 * same `my-organizations` query the workspace switcher uses. `null` while
 * loading or in the personal workspace.
 */
export interface OrgRoleInfo {
  role: OrganizationMemberRole | null;
  /** Custom role permissions, when the member has one (they replace the role defaults). */
  permissions: string[] | null;
  isLoading: boolean;
}

export function useSelectedOrgRole(): OrgRoleInfo {
  const { selectedOrgId } = useUIStore();
  const enabled = !!selectedOrgId;
  const { data, isLoading } = useQuery({
    queryKey: ['my-organizations'],
    queryFn: () => OrganizationApiService.getMyOrganizations(),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
  const membership = data?.find(
    (m) => m.organization.id === selectedOrgId && m.status === 'ACTIVE'
  );
  return {
    role: membership?.role ?? null,
    permissions: membership?.customRole?.permissions ?? null,
    isLoading: enabled && isLoading,
  };
}

/** As useSelectedOrgRole, but only inside a college workspace. */
export function useCollegeRole(): OrgRoleInfo {
  const { selectedOrgType } = useUIStore();
  const r = useSelectedOrgRole();
  return selectedOrgType === 'COLLEGE' ? r : { role: null, permissions: null, isLoading: false };
}

/** As useSelectedOrgRole, but only inside a company workspace. */
export function useCompanyRole(): OrgRoleInfo {
  const { selectedOrgType } = useUIStore();
  const r = useSelectedOrgRole();
  return selectedOrgType === 'COMPANY' ? r : { role: null, permissions: null, isLoading: false };
}
